import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/logger';

// ============================================
// FINANCE SUMMARY
// ============================================

/**
 * Get monthly finance summary with revenue breakdown
 * Returns billed/paid/unpaid amounts per month
 */
export const useFinanceMetrics = (branchId, year) => {
  return useQuery({
    queryKey: ['finance', 'summary', branchId, year],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_finance_summary', {
        p_branch_id: branchId,
        p_year: year
      });

      if (error) {
        logger.error('Failed to fetch finance summary:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!branchId && !!year,
    staleTime: 5 * 60 * 1000 // 5 minutes
  });
};

// ============================================
// REVENUE BY COST ELEMENT
// ============================================

/**
 * Get revenue breakdown by cost element
 * Returns percentage and total for each cost type
 */
export const useRevenueStructure = (branchId, startDate, endDate) => {
  return useQuery({
    queryKey: ['finance', 'revenue-structure', branchId, startDate, endDate],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_revenue_by_cost_element', {
        p_branch_id: branchId,
        p_start_date: startDate,
        p_end_date: endDate
      });

      if (error) {
        logger.error('Failed to fetch revenue structure:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!branchId && !!startDate && !!endDate,
    staleTime: 5 * 60 * 1000
  });
};

// ============================================
// PAYMENT CYCLE
// ============================================

/**
 * Get average payment cycle (days from sent to paid)
 */
export const usePaymentCycle = (branchId, startDate, endDate) => {
  return useQuery({
    queryKey: ['finance', 'payment-cycle', branchId, startDate, endDate],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_payment_cycle_avg', {
        p_branch_id: branchId,
        p_start_date: startDate,
        p_end_date: endDate
      });

      if (error) {
        logger.error('Failed to fetch payment cycle:', error);
        throw error;
      }

      return data?.[0] || { avg_days: null, sample_count: 0 };
    },
    enabled: !!branchId && !!startDate && !!endDate,
    staleTime: 5 * 60 * 1000
  });
};

/**
 * Get payment deadline days from branch settings
 */
export const usePaymentDeadline = (branchId) => {
  return useQuery({
    queryKey: ['finance', 'payment-deadline', branchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('branches')
        .select('billing_settings')
        .eq('id', branchId)
        .single();

      if (error) throw error;
      return data?.billing_settings?.payment_deadline_days || 14;
    },
    enabled: !!branchId,
    staleTime: 10 * 60 * 1000
  });
};

// ============================================
// ARREARS TREND
// ============================================

/**
 * Get monthly arrears trend (count and amount)
 */
export const useArrearsTrend = (branchId, year) => {
  return useQuery({
    queryKey: ['finance', 'arrears-trend', branchId, year],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_arrears_trend', {
        p_branch_id: branchId,
        p_year: year
      });

      if (error) {
        logger.error('Failed to fetch arrears trend:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!branchId && !!year,
    staleTime: 5 * 60 * 1000
  });
};

// ============================================
// ARREARS LIST
// ============================================

/**
 * Get detailed list of overdue receipts
 */
export const useArrearsList = (branchId) => {
  return useQuery({
    queryKey: ['finance', 'arrears-list', branchId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_arrears_list', {
        p_branch_id: branchId
      });

      if (error) {
        logger.error('Failed to fetch arrears list:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!branchId,
    staleTime: 2 * 60 * 1000 // 2 minutes - more frequent updates for arrears
  });
};

// ============================================
// FINANCE ANOMALIES
// ============================================

/**
 * Get counts of finance anomalies (unsent receipts, no pricing, disputes, credit notes)
 */
export const useFinanceAnomalies = (branchId, billingPeriodId = null) => {
  return useQuery({
    queryKey: ['finance', 'anomalies', branchId, billingPeriodId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_finance_anomalies', {
        p_branch_id: branchId,
        p_billing_period_id: billingPeriodId
      });

      if (error) {
        logger.error('Failed to fetch finance anomalies:', error);
        throw error;
      }

      return data?.[0] || {
        unsent_receipts_count: 0,
        children_without_pricing: 0,
        open_disputes_count: 0,
        credit_notes_count: 0,
        credit_notes_total_cents: 0
      };
    },
    enabled: !!branchId,
    staleTime: 2 * 60 * 1000
  });
};

// ============================================
// SEND PAYMENT REMINDER
// ============================================

/**
 * Send payment reminders to selected receipts
 */
export const useSendPaymentReminder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ receiptIds, branchId }) => {
      const { data, error } = await supabase.functions.invoke('send-payment-reminder', {
        body: {
          receipt_ids: receiptIds,
          branch_id: branchId
        }
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Failed to send reminders');

      return data;
    },
    onSuccess: (_, { branchId }) => {
      // Invalidate arrears list to refresh reminder counts
      queryClient.invalidateQueries({ queryKey: ['finance', 'arrears-list', branchId] });
    },
    onError: (error) => {
      logger.error('Failed to send payment reminders:', error);
    }
  });
};

// ============================================
// DISPUTED RECEIPTS
// ============================================

/**
 * Get list of receipts with open disputes
 */
export const useDisputedReceipts = (branchId) => {
  return useQuery({
    queryKey: ['finance', 'disputed-receipts', branchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('receipt_disputes')
        .select(`
          id,
          status,
          reason,
          resolution_notes,
          created_at,
          receipt:parent_receipts!inner(
            id,
            receipt_number,
            total_amount_cents,
            child:children!inner(first_name, last_name),
            parent:app_users!parent_id(first_name, last_name),
            billing_period:billing_periods!inner(name, branch_id)
          )
        `)
        .eq('receipt.billing_period.branch_id', branchId)
        .in('status', ['open', 'in_review'])
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Failed to fetch disputed receipts:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!branchId,
    staleTime: 2 * 60 * 1000
  });
};

// ============================================
// BILLING PERIODS FOR FILTER
// ============================================

/**
 * Get billing periods for filter dropdown
 */
export const useBillingPeriods = (branchId, year = null) => {
  return useQuery({
    queryKey: ['finance', 'billing-periods', branchId, year],
    queryFn: async () => {
      let query = supabase
        .from('billing_periods')
        .select('id, name, start_date, end_date, status')
        .eq('branch_id', branchId)
        .order('start_date', { ascending: false });

      if (year) {
        query = query
          .gte('start_date', `${year}-01-01`)
          .lte('end_date', `${year}-12-31`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    },
    enabled: !!branchId,
    staleTime: 10 * 60 * 1000
  });
};

// ============================================
// HELPER: Calculate YTD totals from monthly data
// ============================================

/**
 * Calculate YTD totals from finance summary data
 */
export const calculateYtdTotals = (monthlyData) => {
  if (!monthlyData || monthlyData.length === 0) {
    return {
      totalRevenue: 0,
      totalBilled: 0,
      totalPaid: 0,
      totalUnpaid: 0,
      receiptCount: 0,
      paidCount: 0
    };
  }

  return monthlyData.reduce((acc, month) => ({
    totalRevenue: acc.totalRevenue + (month.paid_amount_cents || 0),
    totalBilled: acc.totalBilled + (month.billed_amount_cents || 0),
    totalPaid: acc.totalPaid + (month.paid_amount_cents || 0),
    totalUnpaid: acc.totalUnpaid + (month.unpaid_amount_cents || 0),
    receiptCount: acc.receiptCount + (month.receipt_count || 0),
    paidCount: acc.paidCount + (month.paid_count || 0)
  }), {
    totalRevenue: 0,
    totalBilled: 0,
    totalPaid: 0,
    totalUnpaid: 0,
    receiptCount: 0,
    paidCount: 0
  });
};

/**
 * Get last month's data and calculate change percentage
 */
export const getLastMonthStats = (monthlyData) => {
  if (!monthlyData || monthlyData.length === 0) {
    return { lastMonth: null, previousMonth: null, changePercent: null };
  }

  const currentMonth = new Date().getMonth() + 1;
  const lastMonth = monthlyData.find(m => m.month === currentMonth - 1) ||
                    monthlyData[monthlyData.length - 1];
  const previousMonth = monthlyData.find(m => m.month === (lastMonth?.month || 0) - 1);

  let changePercent = null;
  if (lastMonth && previousMonth && previousMonth.paid_amount_cents > 0) {
    changePercent = ((lastMonth.paid_amount_cents - previousMonth.paid_amount_cents) /
                     previousMonth.paid_amount_cents) * 100;
  }

  return { lastMonth, previousMonth, changePercent };
};
